/**
 * All Crowd Count computations in one place.
 * Raw load + UI toggles → compute() → { controls, preview, stats, … }.
 * Charts/preview only render that object.
 *
 * DetectFaces → "Face" boxes (gender/age/emotions/attrs).
 * DetectLabels → "Person" (+ Adult/Male/…) — do not reconcile counts.
 */
import {
  AGE_BUCKETS,
  GENDER_FEMALE_COLOR,
  GENDER_MALE_COLOR,
} from '../Analytics/CrowdCountSection/constants';
import { can } from '../../components/Authorize';
import { CONFIG } from '../../components/Authorize/permissions/resources';
import { buildBlueprint, normalizeCam, resolveArrangeLayout } from '../Mapping/Arrange/logic';
import {
  BOOL_FACE_ATTRS,
  FACE,
  PERSON,
  TILE_CSS_H,
  TILE_CSS_W,
  fmtTilePos,
  fmtTileRC,
  scanIdsFromOption,
  sortTiles,
  tileBoxesKey,
} from './helpers';
import { span as perfSpan, now as perfNow } from './perf';
import { roundToSrs } from './srs/geometry';


const canSelectRegions = () => can(CONFIG);

const sortSessionRegions = (regions = []) => (
  [...regions].sort((a, b) => {
    const cam = String(a.camera || '').localeCompare(String(b.camera || ''));
    return cam || String(a.regionId || '').localeCompare(String(b.regionId || ''));
  })
);

const formatSession = (session) => {
  const ms = Number(session);
  if (!Number.isFinite(ms)) return String(session);
  const d = new Date(ms);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

/** Session = header; region rows indented under it. */
const buildScanOptions = (sessions, { includeRegions = false } = {}) => (
  (sessions || []).flatMap((s) => {
    const regions = sortSessionRegions(s.regions || []);
    if (!regions.length) return [];
    const session = String(s.session);
    const sessionOption = {
      value: `session:${session}`,
      label: formatSession(session),
      kind: 'session',
      session,
      regionIds: regions.map((r) => String(r._id)),
    };
    if (!includeRegions) return [sessionOption];
    return [
      sessionOption,
      ...regions.map((r) => ({
        value: `region:${r._id}`,
        label: `${r.camera} - ${r.regionId}`,
        kind: 'region',
        id: String(r._id),
        session,
        rows: r.rows,
        cols: r.cols,
        camera: r.camera,
        regionId: r.regionId,
      })),
    ];
  })
);

const flatScanOptions = (items) => (items || []).flatMap((item) => (
  Array.isArray(item?.options) ? item.options : [item]
));

const defaultScanOption = (options) => {
  const flat = flatScanOptions(options);
  return flat.find((o) => o.kind === 'session') || flat[0] || null;
};

/** Initial option from session list (page shell uses this once after fetch). */
const defaultOptionForSessions = (sessions) => defaultScanOption(
  buildScanOptions(sessions, { includeRegions: canSelectRegions() }),
);

/**
 * Resolve ?scan=… to a scan option.
 * Prefer unique `value` (`region:<id>` / `session:<ms>`) so the same camera-region
 * under a past session does not resolve to the latest. Bare labels still match
 * the first option (legacy deep links).
 */
const findScanOption = (sessions, scanParam) => {
  const needle = String(scanParam || '');
  if (!needle) return null;
  // Always include regions so deep links like ?scan=cam1%20-%20regionId resolve.
  const options = flatScanOptions(
    buildScanOptions(sessions, { includeRegions: true }),
  );
  return options.find((o) => o.value === needle)
    || options.find((o) => o.label === needle)
    || null;
};

const countableDetectables = (tileBoxes) => {
  const counts = new Map();
  Object.values(tileBoxes || {}).forEach((boxes) => {
    (boxes || []).forEach((b) => {
      if (!b?.label) return;
      counts.set(b.label, (counts.get(b.label) || 0) + 1);
    });
  });
  return Array.from(counts.entries())
    .sort(([aName, aCount], [bName, bCount]) => {
      const aPinned = aName === FACE || aName === PERSON;
      const bPinned = bName === FACE || bName === PERSON;
      if (aPinned !== bPinned) return aPinned ? -1 : 1;
      return bCount - aCount || aName.localeCompare(bName);
    })
    .map(([name, count]) => ({ value: name, label: `${name} (${count})` }));
};

const defaultSelectedLabels = (tileBoxes) => countableDetectables(tileBoxes).map((o) => o.value);

/** Next/prev scan option from current controls. */
const nextScanOption = (scanOptions, optionValue, delta) => {
  const flat = flatScanOptions(scanOptions);
  if (flat.length < 2) return null;
  const i = Math.max(0, flat.findIndex((o) => o.value === optionValue));
  return flat[(i + delta + flat.length) % flat.length];
};

/**
 * Stadium = same blueprint as Arrange (exact sizes + poses).
 * Tiles paint inside those rectangles; Map fit=contain scales the sheet.
 */
const stadiumContentLayout = (entries = []) => {
  const list = entries || [];
  if (!list.length) {
    return { stageW: TILE_CSS_W, stageH: TILE_CSS_H, slots: [] };
  }
  const regions = list.map((entry, i) => ({
    id: entry.scanId || `slot-${i}`,
    camIdx: entry.camera,
    regionId: entry.regionId,
    label: entry.label,
    tileWidth: entry.tileWidth,
    tileHeight: entry.tileHeight,
    rows: entry.rows,
    cols: entry.cols,
    arrangeLayout: entry.arrangeLayout,
  }));
  const { slots, stageW, stageH } = buildBlueprint(regions);
  if (!slots.length) {
    return { stageW: TILE_CSS_W, stageH: TILE_CSS_H, slots: [] };
  }
  const byId = new Map(list.map((e, i) => [e.scanId || `slot-${i}`, e]));
  return {
    stageW: Math.max(1, Math.ceil(stageW)),
    stageH: Math.max(1, Math.ceil(stageH)),
    slots: slots.map((slot) => {
      const entry = byId.get(slot.id) || {};
      return {
        ...entry,
        width: slot.width,
        height: slot.height,
        angle: slot.angle,
        left: slot.left,
        top: slot.top,
      };
    }),
  };
};

const BINS = 100;

const ATTR_KEYS = {
  Smile: 'smile',
  Eyeglasses: 'eyeglasses',
  Sunglasses: 'sunglasses',
  Beard: 'beard',
  Mustache: 'mustache',
  EyesOpen: 'eyesOpen',
  MouthOpen: 'mouthOpen',
  FaceOccluded: 'faceOccluded',
};

const ATTR_LABELS = {
  Smile: 'Smile',
  Eyeglasses: 'Eyeglasses',
  Sunglasses: 'Sunglasses',
  Beard: 'Beard',
  Mustache: 'Mustache',
  EyesOpen: 'Eyes open',
  MouthOpen: 'Mouth open',
  FaceOccluded: 'Face occluded',
};

const ATTR_COLOR = '#4E9BF6';

const EMOTION_COLORS = {
  HAPPY: '#E8A317',
  CALM: '#3D9B8F',
  SURPRISED: '#E07A5F',
  SAD: '#5B8DB8',
  ANGRY: '#D64545',
  FEAR: '#6E7F91',
  CONFUSED: '#C4A35A',
  DISGUSTED: '#7A9E7E',
  UNKNOWN: '#A8ADB6',
};

const DEMO_COLORS = {
  Age: '#AFBD60',
  Adult: '#F2A86B',
  Teen: '#6CC4C1',
  Child: '#7E8ED9',
  Gender: '#9AA3B2',
  Male: GENDER_MALE_COLOR,
  Man: GENDER_MALE_COLOR,
  Boy: GENDER_MALE_COLOR,
  Female: GENDER_FEMALE_COLOR,
  Woman: GENDER_FEMALE_COLOR,
  Girl: GENDER_FEMALE_COLOR,
};

const emotionColor = (name) => (
  EMOTION_COLORS[String(name || '').toUpperCase()] || EMOTION_COLORS.UNKNOWN
);

const titleCase = (value) => String(value || 'Unknown')
  .toLowerCase()
  .replace(/\b\w/g, (c) => c.toUpperCase());

const formatPct = (n, total) => {
  if (!total) return '0%';
  return `${Math.round((n / total) * 100)}%`;
};

const collectBoxes = (tileBoxes) => Object.values(tileBoxes || {}).flat();

const countMapEntries = (map, sortFn) => {
  const entries = [...map.entries()];
  if (sortFn) entries.sort(sortFn);
  else entries.sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0])));
  return entries;
};

const densityFromValues = (values) => {
  const counts = new Array(BINS).fill(0);
  values.forEach((v) => {
    if (!Number.isFinite(v)) return;
    const c = Math.min(99.999, Math.max(0, v));
    counts[Math.min(BINS - 1, Math.floor(c))] += 1;
  });
  return counts;
};

const binsToSeries = (bins) => (bins || []).map((count, pct) => ({
  pct,
  count,
  // Log can't use 0 — floor empty bins so the curve stays continuous from the left.
  y: Math.max(count, 1),
}));

const ageBucketFromRange = (low, high) => {
  const mid = (Number(low) + Number(high)) / 2;
  if (!Number.isFinite(mid)) return null;
  if (mid < 25) return '< 25';
  if (mid < 35) return '25 - 34';
  if (mid < 45) return '35 - 44';
  if (mid < 55) return '45 - 54';
  if (mid < 65) return '55 - 64';
  return '> 65';
};

const parseTileKey = (key) => {
  const raw = String(key).includes(':') ? String(key).split(':').pop() : String(key);
  const [row, col] = raw.split('-').map(Number);
  return {
    row: Number.isFinite(row) ? row : -1,
    col: Number.isFinite(col) ? col : -1,
  };
};

const joinList = (items) => (items?.length ? items.join(', ') : '—');

const avg = (nums) => {
  if (!nums.length) return null;
  return nums.reduce((s, n) => s + n, 0) / nums.length;
};

const pctOf = (part, whole) => (whole ? Math.round((part / whole) * 1000) / 10 : 0);

const buildLabelCatalog = (tileBoxes, observations = []) => {
  const map = new Map();

  const ensure = (name) => {
    let entry = map.get(name);
    if (!entry) {
      entry = {
        name,
        count: 0,
        confidences: [],
        parents: [],
        aliases: new Set(),
        categories: new Set(),
      };
      map.set(name, entry);
    }
    return entry;
  };

  Object.values(tileBoxes || {}).forEach((boxes) => {
    (boxes || []).forEach((b) => {
      if (!b?.label) return;
      const entry = ensure(b.label);
      entry.count += 1;
      if (Number.isFinite(b.confidence)) entry.confidences.push(b.confidence);
    });
  });

  (observations || []).forEach((obs) => {
    if (!obs?.name) return;
    const entry = map.get(obs.name);
    if (!entry) return;
    if ((obs.parents || []).length) {
      entry.parents = [...obs.parents];
    }
    (obs.aliases || []).forEach((a) => entry.aliases.add(a));
    (obs.categories || []).forEach((c) => entry.categories.add(c));
  });

  return Array.from(map.values())
    .map((entry) => ({
      name: entry.name,
      count: entry.count,
      avgConfidence: avg(entry.confidences),
      minConfidence: entry.confidences.length ? Math.min(...entry.confidences) : null,
      maxConfidence: entry.confidences.length ? Math.max(...entry.confidences) : null,
      parents: entry.parents,
      aliases: Array.from(entry.aliases).sort((a, b) => a.localeCompare(b)),
      categories: Array.from(entry.categories).sort((a, b) => a.localeCompare(b)),
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
};

const buildPersonDemoTrees = (catalog) => {
  const counts = new Map((catalog || []).map((e) => [e.name, e.count || 0]));
  const n = (name) => counts.get(name) || 0;

  const sizeFor = (count, maxCount) => {
    const minR = 10;
    const maxR = 52;
    const t = Math.sqrt((count || 0) / (maxCount || 1));
    return Math.round(minR + t * (maxR - minR));
  };

  const make = (name, count, parentTotal, maxCount, children) => {
    const pct = pctOf(count, parentTotal);
    return {
      name,
      count,
      pct,
      value: Math.max(count || 0, 1),
      symbolSize: sizeFor(count, maxCount),
      itemStyle: { color: DEMO_COLORS[name] },
      label: { formatter: `${name}  (${count} · ${pct}%)` },
      children: children?.length ? children : undefined,
    };
  };

  const adult = n('Adult');
  const teen = n('Teen');
  const child = n('Child');
  const ageTotal = adult + teen + child;
  const male = n('Male');
  const female = n('Female');
  const genderTotal = male + female;
  const man = n('Man');
  const woman = n('Woman');
  const boy = n('Boy');
  const girl = n('Girl');
  const ageMax = Math.max(adult, teen, child, man, woman, 1);
  const genderMax = Math.max(male, female, man, woman, boy, girl, 1);

  return {
    ageRoot: {
      name: 'Age',
      count: ageTotal,
      pct: 100,
      value: Math.max(ageTotal, 1),
      symbolSize: sizeFor(ageTotal, ageMax),
      itemStyle: { color: DEMO_COLORS.Age },
      label: { formatter: `Age  (${ageTotal})` },
      children: [
        make('Adult', adult, ageTotal, ageMax, [
          make('Man', man, adult, ageMax),
          make('Woman', woman, adult, ageMax),
        ]),
        make('Teen', teen, ageTotal, ageMax),
        make('Child', child, ageTotal, ageMax),
      ],
    },
    genderRoot: {
      name: 'Gender',
      count: genderTotal,
      pct: 100,
      value: Math.max(genderTotal, 1),
      symbolSize: sizeFor(genderTotal, genderMax),
      itemStyle: { color: DEMO_COLORS.Gender },
      label: { formatter: `Gender  (${genderTotal})` },
      children: [
        make('Male', male, genderTotal, genderMax, [
          make('Man', man, male, genderMax),
          make('Boy', boy, male, genderMax),
        ]),
        make('Female', female, genderTotal, genderMax, [
          make('Woman', woman, female, genderMax),
          make('Girl', girl, female, genderMax),
        ]),
      ],
    },
    personCount: n('Person'),
  };
};

const coverageGrid = (tileBoxes, label, rows, cols) => {
  const grid = Array.from({ length: rows }, () => new Array(cols).fill(0));
  Object.entries(tileBoxes || {}).forEach(([key, list]) => {
    const { row, col } = parseTileKey(key);
    if (row < 0 || col < 0 || row >= rows || col >= cols) return;
    grid[row][col] += list.filter((b) => b.label === label).length;
  });
  return grid;
};

const buildSections = (overlay, tiles, tileBoxes) => {
  const out = {};
  const row = (name) => {
    if (String(name || '').trim().toUpperCase() === 'BRIZI') return null;
    const key = `section-${name || '?'}`;
    if (!out[key]) {
      out[key] = {
        total: 0, filled: 0, faces: 0, gender: { male: 0, female: 0 }, ageRanges: {},
      };
    }
    return out[key];
  };

  (overlay?.seats || []).forEach((s) => {
    const r = row(s.section);
    if (r) r.total += 1;
  });

  const seen = new Set();
  const take = (tile, box) => {
    if (!box.BoundingBox) return;
    const srs = roundToSrs(tile, box.BoundingBox, overlay);
    if (!srs || srs.length < 3) return;
    const id = `${srs[0][1]}|${srs[1][1]}|${srs[2][1]}`;
    if (seen.has(id)) return;
    seen.add(id);
    const r = row(srs[0][1]);
    if (!r) return;
    r.filled += 1;
    if (box.label !== FACE) return;
    r.faces += 1;
    if (box.gender === 'Male') r.gender.male += 1;
    if (box.gender === 'Female') r.gender.female += 1;
    if (Number.isFinite(box.ageLow) && Number.isFinite(box.ageHigh)) {
      const k = `${box.ageLow}-${box.ageHigh}`;
      r.ageRanges[k] = (r.ageRanges[k] || 0) + 1;
    }
  };

  (tiles || []).forEach((tile) => {
    const boxes = tileBoxes?.[tileBoxesKey(tile)] || [];
    boxes.filter((b) => b.label === FACE).forEach((b) => take(tile, b));
    boxes.filter((b) => b.label === PERSON).forEach((b) => take(tile, b));
  });

  Object.keys(out).forEach((key) => {
    const { male, female } = out[key].gender;
    const n = male + female;
    out[key].genderCounts = { male, female };
    if (!n) return;
    out[key] = {
      ...out[key],
      gender: {
        male: Math.round((male / n) * 100),
        female: Math.round((female / n) * 100),
      },
      genderCounts: { male, female },
    };
  });
  return out;
};

const faceAttrRows = (faces) => BOOL_FACE_ATTRS.map((name) => {
  const key = ATTR_KEYS[name];
  let present = 0;
  let known = 0;
  faces.forEach((f) => {
    const v = f[key];
    if (v === true) {
      present += 1;
      known += 1;
    } else if (v === false) {
      known += 1;
    }
  });
  return {
    name: ATTR_LABELS[name] || name,
    present,
    known,
    rate: known ? present / known : 0,
  };
})
  .filter((r) => r.known > 0)
  .sort((a, b) => b.rate - a.rate || a.name.localeCompare(b.name));

const sectionName = (key) => key.replace(/^section-/, '');

/** Precompute age covers + occupancy/gender row props for Section Demographics. */
const buildSectionDemo = (sections = {}) => {
  const names = Object.keys(sections)
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const maxTotal = Math.max(1, ...names.map((k) => sections[k].total));

  let minAge = Infinity;
  let maxAge = -Infinity;
  let maxCover = 1;
  const covers = {};
  names.forEach((key) => {
    const cover = {};
    Object.entries(sections[key].ageRanges || {}).forEach(([range, n]) => {
      const [lo, hi] = range.split('-').map(Number);
      if (!Number.isFinite(lo) || !Number.isFinite(hi)) return;
      for (let age = Math.min(lo, hi); age <= Math.max(lo, hi); age += 1) {
        cover[age] = (cover[age] || 0) + n;
      }
    });
    covers[key] = cover;
    Object.entries(cover).forEach(([age, n]) => {
      minAge = Math.min(minAge, Number(age));
      maxAge = Math.max(maxAge, Number(age));
      maxCover = Math.max(maxCover, n);
    });
  });
  if (!Number.isFinite(minAge)) {
    minAge = 0;
    maxAge = 1;
  }
  const span = Math.max(1, maxAge - minAge);
  const ticks = [];
  for (let age = minAge; age <= maxAge; age += 1) {
    if (age % 10 === 0 || age === minAge || age === maxAge) ticks.push(age);
  }

  const ageRows = names.map((key) => ({
    key,
    name: sectionName(key),
    cover: covers[key],
    ageRanges: sections[key].ageRanges,
  }));

  const occupancyRows = names.map((key) => {
    const r = sections[key];
    const pct = r.total ? Math.round((r.filled / r.total) * 100) : 0;
    return {
      name: sectionName(key),
      width: (r.total / maxTotal) * 100,
      a: r.filled,
      b: Math.max(0, r.total - r.filled),
      end: `${r.filled}/${r.total}`,
      pct: `${pct}%`,
      tip: [
        `Seats: ${r.total}`,
        `Occupied seats: ${r.filled}`,
        `Faces on occupied seats: ${r.faces || 0}`,
        `Occupancy: ${pct}%`,
      ],
    };
  });

  const genderRows = names.map((key) => {
    const r = sections[key];
    const g = r.gender;
    const counts = r.genderCounts || { male: 0, female: 0 };
    const detected = counts.male + counts.female;
    return {
      name: sectionName(key),
      width: 100,
      a: g.male,
      b: g.female,
      tip: [
        `Detected faces: ${detected}`,
        `Male: ${counts.male} (${g.male || 0}%)`,
        `Female: ${counts.female} (${g.female || 0}%)`,
      ],
    };
  });

  return {
    age: {
      minAge,
      maxAge,
      span,
      ticks,
      maxCover,
      hasAges: names.some((k) => Object.keys(covers[k]).length),
      rows: ageRows,
    },
    occupancy: occupancyRows,
    gender: genderRows,
  };
};

/** Domain stats only — charts derive colors / series / trees at paint time. */
const EMPTY_STATS = {
  counts: { faces: 0, persons: 0 },
  gender: {},
  age: { buckets: [], ranges: [] },
  emotions: {},
  confidence: { face: [], person: [], gender: [] },
  faceAttrs: [],
  labels: [],
  coverage: null,
  sections: {
    age: {
      minAge: 0, maxAge: 1, span: 1, ticks: [], maxCover: 1, hasAges: false, rows: [],
    },
    occupancy: [],
    gender: [],
  },
};

const computeStats = ({
  tiles = [],
  tileBoxes,
  rekognitionLabels,
  overlay = null,
  rows = 1,
  cols = 1,
  isGroup = false,
} = {}) => {
  const boxes = collectBoxes(tileBoxes);
  const faces = boxes.filter((b) => b.label === FACE);
  const persons = boxes.filter((b) => b.label === PERSON);
  const labelCatalog = buildLabelCatalog(tileBoxes, rekognitionLabels);

  const gender = {};
  faces.forEach((f) => {
    if (f.gender === 'Male' || f.gender === 'Female') {
      gender[f.gender] = (gender[f.gender] || 0) + 1;
    }
  });

  const ageBucketMap = new Map(AGE_BUCKETS.map((b) => [b, 0]));
  faces.forEach((f) => {
    const bucket = ageBucketFromRange(f.ageLow, f.ageHigh);
    if (!bucket || !ageBucketMap.has(bucket)) return;
    ageBucketMap.set(bucket, ageBucketMap.get(bucket) + 1);
  });
  const ageBuckets = AGE_BUCKETS
    .map((bucket) => ({ name: bucket, value: ageBucketMap.get(bucket) || 0 }))
    .filter(({ value }) => value > 0);

  const ageExactMap = new Map();
  faces.forEach((f) => {
    if (!Number.isFinite(f.ageLow) || !Number.isFinite(f.ageHigh)) return;
    const label = `${f.ageLow}-${f.ageHigh}`;
    ageExactMap.set(label, (ageExactMap.get(label) || 0) + 1);
  });
  const ageRanges = [...ageExactMap.entries()]
    .sort(([a], [b]) => {
      const [aL, aH] = a.split('-').map(Number);
      const [bL, bH] = b.split('-').map(Number);
      return aL - bL || aH - bH;
    })
    .map(([name, value]) => ({ name, value }));

  const emotions = {};
  faces.forEach((b) => {
    const k = b.topEmotion || 'Unknown';
    emotions[k] = (emotions[k] || 0) + 1;
  });

  const rawSections = buildSections(overlay, tiles, tileBoxes);

  return {
    counts: { faces: faces.length, persons: persons.length },
    gender,
    age: { buckets: ageBuckets, ranges: ageRanges },
    emotions,
    confidence: {
      face: densityFromValues(faces.map((f) => f.confidence)),
      person: densityFromValues(persons.map((p) => p.confidence)),
      gender: densityFromValues(
        faces.map((f) => f.genderConfidence).filter((n) => n != null),
      ),
    },
    faceAttrs: faceAttrRows(faces),
    labels: labelCatalog,
    coverage: isGroup ? null : {
      rows,
      cols,
      faces: coverageGrid(tileBoxes, FACE, rows, cols),
      persons: coverageGrid(tileBoxes, PERSON, rows, cols),
    },
    sections: buildSectionDemo(rawSections),
  };
};

/** Process loaded data into one page object (UI selection overlays applied outside). */
const compute = ({
  sessions = [],
  scans = [],
  option = null,
  tileBoxes = {},
  rekognitionLabels = [],
  overlay = null,
  layoutMap = {},
  layoutsReady = false,
  sessionsLoading = false,
  scanLoading = false,
  scanError = false,
  detectionsLoading = false,
  includeStats = true,
} = {}) => {
  const t0 = perfNow();
  const scanOptions = buildScanOptions(sessions, { includeRegions: canSelectRegions() });
  const flatOptions = flatScanOptions(scanOptions);
  const optionValue = option?.value || '';
  const scanIds = scanIdsFromOption(option);
  const isGroup = option?.kind === 'session' && scanIds.length >= 2;
  const singleScan = !isGroup ? (scans[0] || null) : null;

  const tiles = isGroup
    ? scans.flatMap((scan) => {
      const scanId = String(scan._id);
      return sortTiles(scan.tiles).map((t) => ({ ...t, _scanId: scanId }));
    })
    : sortTiles(singleScan?.tiles);

  const detectableOptions = countableDetectables(tileBoxes);

  const stadiumEntries = (!isGroup || !layoutsReady) ? [] : scanIds.map((id) => {
    const meta = sessions.find((s) => String(s.session) === option.session);
    const scan = scans.find((d) => String(d._id) === id) || null;
    const region = (meta?.regions || []).find((r) => String(r._id) === id);
    const cam = normalizeCam(region?.camera || scan?.camera);
    const rid = String(region?.regionId || scan?.regionId || '').trim() || null;
    const camMeta = resolveArrangeLayout(layoutMap, cam, rid) || {};
    const hasPose = Number.isFinite(Number(camMeta.x)) && Number.isFinite(Number(camMeta.y));
    return {
      scanId: id,
      scan,
      tiles: sortTiles(scan?.tiles),
      rows: Number(scan?.rows) || Number(region?.rows) || Number(camMeta.rows) || 0,
      cols: Number(scan?.cols) || Number(region?.cols) || Number(camMeta.cols) || 0,
      tileWidth: Number(scan?.tileWidth)
        || Number(region?.tileWidth)
        || Number(camMeta.tileWidth)
        || null,
      tileHeight: Number(scan?.tileHeight)
        || Number(region?.tileHeight)
        || Number(camMeta.tileHeight)
        || null,
      label: region ? `${region.camera} · ${region.regionId}` : (scan?.camera || id),
      camera: cam,
      regionId: rid,
      arrangeLayout: hasPose
        ? { x: camMeta.x, y: camMeta.y, angle: camMeta.angle || 0 }
        : null,
    };
  });

  const cols = Number(singleScan?.cols) || Number(option?.cols) || 0;
  const rows = Number(singleScan?.rows) || Number(option?.rows) || 0;
  const shimmer = !isGroup && scanLoading && rows > 0 && cols > 0;
  const showRegion = shimmer || (!isGroup && !scanLoading && tiles.length > 0);
  const showStadium = isGroup && (
    scanLoading || stadiumEntries.some((e) => e.tiles.length || (e.rows && e.cols))
  );

  const tileByCell = {};
  tiles.forEach((tile) => {
    tileByCell[`${tile.row}-${tile.col}`] = tile;
  });

  const ready = !detectionsLoading && ((isGroup && scans.length > 0) || (!isGroup && !!singleScan));

  // Charts are expensive (boxes × seats). Skip until detections are ready and charts mount.
  const tStats = perfNow();
  const stats = (includeStats && ready)
    ? computeStats({
      tiles,
      tileBoxes,
      rekognitionLabels,
      overlay,
      rows: isGroup ? 1 : (singleScan?.rows || rows),
      cols: isGroup ? 1 : (singleScan?.cols || cols),
      isGroup,
    })
    : EMPTY_STATS;

  if (includeStats && ready) perfSpan('compute.stats', tStats);

  const out = {
    loading: {
      sessions: sessionsLoading,
      scan: scanLoading,
      detections: detectionsLoading,
    },
    error: { scan: scanError },
    ui: { showBoxes: false, srsShow: false },
    controls: {
      scanOptions,
      option,
      optionValue,
      hasScans: flatOptions.length > 0,
      canNavigate: flatOptions.length > 1,
      detectableOptions,
      selectedOptions: [],
    },
    preview: {
      isGroup,
      showStadium,
      showRegion,
      shimmer,
      singleScan,
      tiles,
      rows,
      cols,
      tileBoxes,
      selectedLabelSet: new Set(),
      stadiumEntries,
      stadiumLayout: stadiumContentLayout(stadiumEntries),
      tileByCell,
      overlay,
      rekognitionLabels,
    },
    stats,
    ready,
  };

  perfSpan('compute.total', t0, {
    tiles: tiles.length,
    boxes: Object.values(tileBoxes || {}).reduce((n, a) => n + (a?.length || 0), 0),
    seats: overlay?.seats?.length || 0,
    includeStats: Boolean(includeStats && ready),
  });

  return out;
};

const labelDetails = (entry) => {
  let confidence = '—';
  if (entry.avgConfidence != null) {
    confidence = `avg ${entry.avgConfidence.toFixed(1)}%`;
    if (entry.minConfidence != null && entry.maxConfidence != null) {
      confidence += ` (${entry.minConfidence.toFixed(1)}–${entry.maxConfidence.toFixed(1)}%)`;
    }
  }
  return [
    ['Count', String(entry.count)],
    ['Confidence', confidence],
    ['Parents', joinList(entry.parents)],
    ['Aliases', joinList(entry.aliases)],
    ['Categories', joinList(entry.categories)],
  ];
};

/** Rank Rekognition age ranges by count (section demographics hover + drawer). */
const rankAgeRanges = (ageRanges = {}) => (
  Object.entries(ageRanges)
    .map(([range, n]) => ({ range, n: Number(n) || 0 }))
    .sort((a, b) => b.n - a.n || a.range.localeCompare(b.range, undefined, { numeric: true }))
);

/** Click Age-by-Section row → detail drawer payload (full range list). */
const sectionAgeDetail = (row, { age, overlap } = {}) => {
  if (!row) return null;
  const ranked = rankAgeRanges(row.ageRanges);
  const people = ranked.reduce((s, r) => s + r.n, 0);
  const year = Number.isFinite(age) ? age : null;
  const atYear = year != null ? Number(overlap) || 0 : null;
  return {
    kind: 'section-age',
    title: `Section ${row.name}`,
    subtitle: year != null
      ? `Age ${year} · ${people} people`
      : `${people} people`,
    rows: [
      ['People', String(people)],
      ...(year != null
        ? [[`Age ${year}`, `${atYear} estimate${atYear === 1 ? '' : 's'}`]]
        : []),
      ...ranked.map((r) => [r.range, String(r.n)]),
    ],
  };
};

/** Detail payload helpers used by chart click handlers (no compute). */
const openLabelDetail = (entry, onOpenDetail) => {
  if (!entry) return;
  onOpenDetail?.({
    kind: 'label',
    title: entry.name,
    subtitle: `${entry.count} boxes`,
    rows: labelDetails(entry),
    raw: {
      name: entry.name,
      count: entry.count,
      avgConfidence: entry.avgConfidence,
      minConfidence: entry.minConfidence,
      maxConfidence: entry.maxConfidence,
      parents: entry.parents,
      aliases: entry.aliases,
      categories: entry.categories,
    },
  });
};

/** Tile heatmap click → detail drawer payload (pure). */
const tileCoverageDetail = (preview, row, col, kind) => {
  const tilesAtCell = (preview?.tiles || []).filter(
    (t) => Number(t.row) === Number(row) && Number(t.col) === Number(col),
  );
  const tile = tilesAtCell[0] || preview?.tileByCell?.[`${row}-${col}`];
  const focusLabel = kind === PERSON ? PERSON : FACE;
  const focused = tilesAtCell.flatMap((t) => (
    preview?.tileBoxes?.[tileBoxesKey(t)] || []
  ).filter((b) => b.label === focusLabel && b.BoundingBox));
  const count = focused.length;
  const kindLabel = focusLabel === PERSON ? 'persons' : 'faces';
  const faceDetails = focusLabel === FACE
    ? focused.filter((b) => b.raw).map((b) => b.raw)
    : [];
  const labelPayloads = focusLabel === PERSON
    ? tilesAtCell.flatMap((t) => (
      preview?.rekognitionLabels || []
    ).filter((obs) => obs.tileKey === tileBoxesKey(t) && obs.raw).map((obs) => obs.raw))
    : [];

  return {
    kind: 'tile',
    title: `Tile ${fmtTileRC(row, col)}`,
    subtitle: tile
      ? `${count} ${kindLabel}`
      : 'No tile image for this cell',
    tileUrl: tile?.url || null,
    boxes: focused,
    rows: [
      ['Position', fmtTileRC(row, col)],
      ...(tile?.capturedAt
        ? [['Captured', new Date(tile.capturedAt).toLocaleString()]]
        : []),
      [focusLabel === PERSON ? 'Persons' : 'Faces', String(count)],
    ],
    rawLabel: 'AWS Rekognition',
    raw: focusLabel === FACE
      ? { FaceDetails: faceDetails }
      : { Labels: labelPayloads },
  };
};

/** Canvas / hit-target click → detection detail payload (pure).
 * Always uses tile.url (full-res original), never webp preview — sidebar crops from that. */
const detectionDetail = (box, tile) => {
  if (!box || !tile) return null;
  const fullUrl = tile.url || null;
  return {
    kind: 'detection',
    title: box.label,
    subtitle: `Tile ${fmtTilePos(tile)}`,
    tileUrl: fullUrl,
    bbox: box.BoundingBox,
    tile,
    rows: [
      ...(box.details || []),
      ['Tile', fmtTilePos(tile)],
      ...(tile.capturedAt
        ? [['Captured', new Date(tile.capturedAt).toLocaleString()]]
        : []),
    ],
    raw: box.raw || null,
    rawLabel: 'AWS Rekognition',
  };
};

/**
 * Pick topmost detection under a point in a mosaic cell (atlas pixel space).
 * Prefers smallest-area box (same idea as DOM z-index stacking).
 * Hit box matches the painted BoundingBox exactly — any selected label is clickable.
 */
const pickDetectionAt = ({
  tiles, rows, cols, tileBoxes, selectedLabels, boxKeyPrefix, x, y, atlasW, atlasH,
}) => {
  const r = Number(rows) || 0;
  const c = Number(cols) || 0;
  if (!r || !c || !atlasW || !atlasH || !selectedLabels?.size) return null;
  const cellW = atlasW / c;
  const cellH = atlasH / r;
  if (cellW <= 0 || cellH <= 0) return null;
  const col = Math.floor(x / cellW);
  const row = Math.floor(y / cellH);
  if (row < 0 || col < 0 || row >= r || col >= c) return null;
  const tile = (tiles || []).find((t) => Number(t.row) === row && Number(t.col) === col);
  if (!tile) return null;
  const boxes = tileBoxes?.[tileBoxesKey(tile, boxKeyPrefix)] || [];
  const lx = (x - col * cellW) / cellW;
  const ly = (y - row * cellH) / cellH;
  let best = null;
  let bestArea = Infinity;
  boxes.forEach((box) => {
    if (!box?.BoundingBox || !selectedLabels.has(box.label)) return;
    const {
      Left = 0, Top = 0, Width = 0, Height = 0,
    } = box.BoundingBox;
    if (lx < Left || ly < Top || lx > Left + Width || ly > Top + Height) return;
    const area = Math.max(0.0001, Width * Height);
    if (area < bestArea) {
      bestArea = area;
      best = box;
    }
  });
  if (!best) return null;
  const tileArg = boxKeyPrefix ? { ...tile, _scanId: boxKeyPrefix } : tile;
  return detectionDetail(best, tileArg);
};


/** Add Section/Row/Seat rows when overlay mapping is loaded (paint toggle independent). */
const annotateDetail = (detail, overlay) => {
  if (detail?.kind !== 'detection' || !detail.tile || !detail.bbox
    || !overlay?.seats?.length || !can(CONFIG)) {
    return detail;
  }
  const rows = roundToSrs(detail.tile, detail.bbox, overlay);
  return rows ? { ...detail, rows: [...rows, ...(detail.rows || [])] } : detail;
};

export default compute;
export { parseRekognition } from './parse';
export {
  computeStats,
  ATTR_COLOR,
  ATTR_COLOR as LABEL_BOX_COLOR,
  emotionColor,
  titleCase,
  formatPct,
  openLabelDetail,
  sectionAgeDetail,
  rankAgeRanges,
  tileCoverageDetail,
  detectionDetail,
  pickDetectionAt,
  annotateDetail,
  buildScanOptions,
  flatScanOptions,
  defaultScanOption,
  defaultOptionForSessions,
  findScanOption,
  defaultSelectedLabels,
  nextScanOption,
  scanIdsFromOption,
  sortTiles,
  sortSessionRegions,
  formatSession,
  countableDetectables,
  stadiumContentLayout,
  // Analytics helpers (formerly explore/data) — kept pure for unit tests.
  ageBucketFromRange,
  buildLabelCatalog,
  buildPersonDemoTrees,
  buildSectionDemo,
  collectBoxes,
  countMapEntries,
  densityFromValues,
  binsToSeries,
  parseTileKey,
};
